'use client'

import { ShoppingBasket } from '../../../icons'
import { cn } from '../../../utils/cn'
import { formatNumber } from '../../../utils/formatNumber'

export interface PurchaseSummaryProps {
  /**
   * Class name
   */
  className?: string

  /**
   * Plan name. Presence of this switches the summary to subscription wording.
   */
  planName?: string

  /**
   * Points granted for the purchase itself. `undefined` renders `- pt` / `¥0`.
   */
  value?: number

  /**
   * Amount charged in yen. Defaults to `value`, which is correct whenever a
   * point equals a yen — for subscription plans the price is a separate field.
   */
  amount?: number

  /**
   * Bonus points granted on top of `value`
   */
  extra?: number

  /**
   * Expiry date, pre-formatted (e.g. `2019/12/18`)
   */
  expireDate?: string

  /**
   * First payment date, pre-formatted (e.g. `7月29日`). Subscription only.
   */
  paymentDate?: string

  /**
   * Day of month for recurring payments, pre-formatted (e.g. `29日`).
   * Subscription only.
   */
  recurringPaymentDay?: string

  /**
   * Small payment handling fee added to the total
   */
  transactionFee?: number
}

export const PurchaseSummary = ({
  className,
  planName,
  value,
  amount,
  extra,
  expireDate,
  paymentDate,
  recurringPaymentDay,
  transactionFee = 0,
}: PurchaseSummaryProps) => {
  const isSubscriptionPlan = !!planName
  const chargeAmount = amount ?? value

  return (
    <div className={cn('w-full', className)}>
      <div className="flex h-9 w-full items-center gap-2 bg-gray-01 px-3 text-white typography-subhead2">
        <ShoppingBasket className="size-6 shrink-0" /> 購入内容
      </div>

      <div className="flex flex-col gap-3 px-6 py-4 text-gray-01">
        {planName && <div className="typography-subhead1">{planName}</div>}

        <div className="border-b border-solid border-gray-02 pb-3">
          <div className="flex items-end typography-subhead1">
            <div>{value ? formatNumber(value) : '-'}</div>
            <div className="ml-1">pt</div>

            {extra && extra !== 0 ? (
              <div className="ml-2 typography-subhead2">
                +特典
                <span className="text-red-01">{formatNumber(extra)}pt</span>
              </div>
            ) : null}
          </div>

          {!isSubscriptionPlan && (
            <div className="mt-1 typography-body2">(有効期限 {expireDate})</div>
          )}

          {isSubscriptionPlan && (
            <div className="mt-1 typography-body2">
              初回の購入日（ポイント付与日）: {paymentDate}
              <br />
              有効期限: 付与日から90日
            </div>
          )}
        </div>

        <div className="flex items-center justify-between text-gray-01">
          <div className="typography-body1">
            {isSubscriptionPlan && <>月々の</>}ご決済金額
          </div>
          <div className="typography-subhead1">
            ¥
            {chargeAmount
              ? formatNumber(
                  chargeAmount + (isSubscriptionPlan ? 0 : transactionFee),
                )
              : 0}
          </div>
        </div>

        {isSubscriptionPlan && (
          <div className="text-gray-01 typography-caption1">
            次回以降の購入日（ポイント付与日）は毎月{recurringPaymentDay}です。
            （応当日がない場合、原則として月末に移動しますが、システムが準拠する基準（UTC基準）により、前後する場合がございます。）
            次回決済予定日はマイページにてご確認いただけます。
          </div>
        )}
      </div>
    </div>
  )
}
